import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';

import { statusConfig, type Status } from './components/badges';
import { SecaoFontesPublicas } from './components/secao-fontes-publicas';
import { SecaoSistemasProcessuais } from './components/secao-sistemas-processuais';
import { SecaoAssinatura } from './components/secao-assinatura';
import { SecaoAPIsComerciais } from './components/secao-apis-comerciais';
import { SecaoFluxos } from './components/secao-fluxos';
import { SecaoDecisoes } from './components/secao-decisoes';

/* ------------------------------------------------------------------ */
/* Diagrama de dependências entre módulos judiciários                   */
/* ------------------------------------------------------------------ */

function DependencyDiagram() {
  const nodes: Array<{ id: string; label: string; status: Status; x: number; y: number }> = [
    { id: 'J04', label: 'J04\nCert Manager', status: 'ativo', x: 50, y: 10 },
    { id: 'J10', label: 'J10\nTribunais', status: 'ativo', x: 250, y: 10 },
    { id: 'J01', label: 'J01\nDJEN', status: 'bypass', x: 10, y: 120 },
    { id: 'J02', label: 'J02\nDataJud', status: 'ativo', x: 190, y: 120 },
    { id: 'J05', label: 'J05\nAdapters', status: 'pendente', x: 370, y: 120 },
    { id: 'J03', label: 'J03\nDJE', status: 'bloqueado', x: 190, y: 220 },
    { id: 'J06', label: 'J06\nSync', status: 'ativo', x: 10, y: 220 },
    { id: 'J09', label: 'J09\nParser', status: 'ativo', x: 370, y: 220 },
    { id: 'J07', label: 'J07\nPrazos', status: 'ativo', x: 10, y: 320 },
    { id: 'J08', label: 'J08\nTermos', status: 'ativo', x: 190, y: 320 },
  ];

  const edges: Array<{ from: string; to: string }> = [
    { from: 'J04', to: 'J05' },
    { from: 'J04', to: 'J03' },
    { from: 'J05', to: 'J03' },
    { from: 'J10', to: 'J02' },
    { from: 'J10', to: 'J05' },
    { from: 'J01', to: 'J06' },
    { from: 'J06', to: 'J07' },
    { from: 'J01', to: 'J09' },
    { from: 'J03', to: 'J09' },
    { from: 'J09', to: 'J07' },
    { from: 'J09', to: 'J08' },
  ];

  const nodeMap = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const BOX_W = 90;
  const BOX_H = 44;

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox="0 0 490 390"
        className="w-full max-w-lg mx-auto"
        style={{ minWidth: 320 }}
        aria-label="Diagrama de dependências entre integrações"
      >
        {edges.map(({ from, to }, i) => {
          const f = nodeMap[from];
          const t = nodeMap[to];
          if (!f || !t) return null;
          const x1 = f.x + BOX_W / 2;
          const y1 = f.y + BOX_H;
          const x2 = t.x + BOX_W / 2;
          const y2 = t.y;
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="oklch(0.7 0.02 270 / 0.35)"
              strokeWidth={1.5}
              strokeDasharray="4 3"
            />
          );
        })}

        {nodes.map((n) => {
          const cfg = statusConfig[n.status];
          const [line1, line2] = n.label.split('\n');
          return (
            <g key={n.id}>
              <rect
                x={n.x}
                y={n.y}
                width={BOX_W}
                height={BOX_H}
                rx={6}
                ry={6}
                className={cfg.bg}
                style={{ fill: 'oklch(1 0 0 / 0.06)', stroke: 'oklch(1 0 0 / 0.18)' }}
              />
              <text
                x={n.x + BOX_W / 2}
                y={n.y + 15}
                textAnchor="middle"
                fontSize={10}
                fontWeight={700}
                fontFamily="monospace"
                fill="oklch(0.98 0 0)"
              >
                {line1}
              </text>
              <text
                x={n.x + BOX_W / 2}
                y={n.y + 29}
                textAnchor="middle"
                fontSize={9}
                fill="oklch(0.82 0.02 270)"
              >
                {line2}
              </text>
              <circle
                cx={n.x + BOX_W - 8}
                cy={n.y + 8}
                r={4}
                className={cfg.dot}
                style={{
                  fill:
                    n.status === 'ativo'
                      ? 'oklch(0.72 0.20 145)'
                      : n.status === 'bypass'
                      ? 'oklch(0.78 0.18 85)'
                      : n.status === 'bloqueado'
                      ? 'oklch(0.65 0.22 25)'
                      : 'oklch(0.60 0.02 270)',
                }}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Resumo de status no topo                                             */
/* ------------------------------------------------------------------ */

const statusCounts: Record<Status, number> = {
  ativo: 7,    // J02, J04, J06, J07, J08, J09, J10
  bypass: 1,   // J01
  bloqueado: 1, // J03
  pendente: 1, // J05
  arquivado: 1, // cert em nuvem
};

function ResumoStatus() {
  const entries = (
    ['ativo', 'bypass', 'bloqueado', 'pendente', 'arquivado'] as Status[]
  ).map((s) => [s, statusCounts[s]] as [Status, number]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Status das integrações</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          {entries.map(([status, count]) => {
            const cfg = statusConfig[status];
            return (
              <div
                key={status}
                className={`rounded-lg p-3 ${cfg.bg} flex items-center justify-between`}
              >
                <div>
                  <p className={`text-xs font-semibold ${cfg.text}`}>{cfg.label}</p>
                  <p className="text-xs text-[var(--fg-muted)] mt-0.5">{cfg.descricao}</p>
                </div>
                <span className={`text-2xl font-bold ${cfg.text} tabular-nums ml-2`}>
                  {count}
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Página principal                                                     */
/* ------------------------------------------------------------------ */

export default function MapaIntegracaoPage() {
  return (
    <div className="min-h-screen bg-[var(--bg)] px-4 py-12 text-[var(--fg)]">
      <div className="mx-auto max-w-4xl space-y-14">

        {/* Hero */}
        <header className="space-y-3">
          <p className="font-mono text-xs uppercase tracking-widest text-[var(--fg-muted)]">
            VoxisAdv — CRM Jurídico
          </p>
          <h1 className="text-3xl font-bold tracking-tight">
            Mapa de Integrações Judiciais
          </h1>
          <p className="max-w-2xl text-base text-[var(--fg-muted)] leading-relaxed">
            Panorama completo das integrações com o Poder Judiciário brasileiro.
            Estruturado em 6 seções: fontes públicas do CNJ, sistemas processuais por sistema, certificação digital, APIs comerciais, fluxos de acionamento e decisões técnicas tomadas.
          </p>
          <p className="text-xs text-[var(--fg-muted)]">
            Atualizado em 2026-05-27 — cliente piloto: Bárbara Bianca (OAB-RS 087113)
          </p>
        </header>

        {/* Sumário de navegação */}
        <nav aria-label="Sumário" className="rounded-lg border border-[var(--card-border)] bg-[var(--surface-muted)]/40 p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">
            Nesta página
          </p>
          <ol className="space-y-1.5">
            {[
              'Seção 1 — Fontes Públicas Unificadas (DJEN, DataJud, DJE)',
              'Seção 2 — Sistemas Processuais por sistema (eProc, PJe, SAJ, outros)',
              'Seção 3 — Assinatura e Certificado Digital',
              'Seção 4 — APIs Comerciais (JUDIT, Escavador, Infosimples)',
              'Seção 5 — Fluxos de Acionamento — quando cada integração roda',
              'Seção 6 — Decisões e Pendências',
            ].map((item, i) => (
              <li key={i} className="flex gap-2 text-sm text-[var(--fg-muted)]">
                <span className="font-mono text-[var(--accent)]">{String(i + 1).padStart(2, '0')}</span>
                {item}
              </li>
            ))}
          </ol>
        </nav>

        {/* Resumo de status */}
        <ResumoStatus />

        {/* Diagrama de dependências */}
        <section aria-label="Diagrama de dependências">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Dependências entre módulos judiciários</CardTitle>
              <CardDescription className="text-xs">
                Setas indicam dependência — módulo na ponta depende do módulo na base. Ponto colorido indica status.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DependencyDiagram />
              <div className="mt-4 flex flex-wrap gap-3 justify-center">
                {(
                  ['ativo', 'bypass', 'bloqueado', 'pendente'] as Status[]
                ).map((s) => {
                  const cfg = statusConfig[s];
                  return (
                    <span key={s} className="flex items-center gap-1.5 text-xs text-[var(--fg-muted)]">
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{
                          background:
                            s === 'ativo'
                              ? 'oklch(0.72 0.20 145)'
                              : s === 'bypass'
                              ? 'oklch(0.78 0.18 85)'
                              : s === 'bloqueado'
                              ? 'oklch(0.65 0.22 25)'
                              : 'oklch(0.60 0.02 270)',
                        }}
                      />
                      {cfg.label}
                    </span>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Separador visual */}
        <div className="border-t border-[var(--border)]" />

        {/* SEÇÃO 1 */}
        <SecaoFontesPublicas />

        <div className="border-t border-[var(--border)]" />

        {/* SEÇÃO 2 */}
        <SecaoSistemasProcessuais />

        <div className="border-t border-[var(--border)]" />

        {/* SEÇÃO 3 */}
        <SecaoAssinatura />

        <div className="border-t border-[var(--border)]" />

        {/* SEÇÃO 4 */}
        <SecaoAPIsComerciais />

        <div className="border-t border-[var(--border)]" />

        {/* SEÇÃO 5 */}
        <SecaoFluxos />

        <div className="border-t border-[var(--border)]" />

        {/* SEÇÃO 6 */}
        <SecaoDecisoes />

        {/* Footer */}
        <footer className="border-t border-[var(--border)] pt-6 text-center text-xs text-[var(--fg-muted)]">
          <p>Documento interno de referência técnica. Não contém credenciais, IPs ou dados sensíveis.</p>
          <p className="mt-1">VoxisAdv — CRM Jurídico SaaS Multi-Tenant &mdash; Fase MVP, 2026</p>
        </footer>

      </div>
    </div>
  );
}
